import * as React from 'react'
import * as Redux from 'react-redux'
import { styled } from 'next-yak'

import { request } from '@owl-nest/api-client/latest'
import { usePoll } from '@owl-nest/hooks/src/usePoll'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'
import { renderConfetti } from '@owl-nest/confetti'
import * as env from '@owl-nest/config'

import * as UFE from '../../../../../UFE'
import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'

import { Orders } from './Orders'
import { Supporters } from './Supporters'
import { DateInfo } from './DateInfo'
import { Progress } from './Progress'
import { StretchGoal } from './StretchGoal'

type RawStats = {
  amount_raised: string
  committed: number
  goal: number
  supporters_count: number
  orders_count: number
}

type Stats = {
  amountRaised: number
  committed: number
  goal: number
  percent: number
  supportersCount: number
  ordersCount: number
}

export function Statistics(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const [stats, setStats] = React.useState<Stats>({
    ordersCount: project.orders_count,
    supportersCount: project.supporters_count,
    amountRaised: project.amount_raised,
    goal: project.goal,
    percent: models.project.progress<projectModel.ExtraFields>(project),
    committed: project.committed,
  })

  const [previousPercent, setPreviousPercent] = React.useState<number>(
    models.project.progress<projectModel.ExtraFields>(project),
  )

  const userConfig = env.useUserEnv()

  const poller = usePoll(async () => {
    const url = new URL(project.live_stats_url)
    url.search = new URLSearchParams({ cachebuster: String(Date.now()) }).toString()
    const response = await request<RawStats>(url.toString())
    const data = response.doThrow()
    // `goal` is not set for some old projects.
    // For these projects, we have to use the already known goal from the project resource.
    const goal = data.body.goal || project.goal

    setStats({
      ordersCount: data.body.orders_count || project.orders_count,
      supportersCount: data.body.supporters_count || project.supporters_count,
      amountRaised: Number(data.body.amount_raised),
      goal: goal,
      percent: goal > 0 ? Math.floor((data.body.committed * 100) / goal) : 0,
      committed: data.body.committed,
    })
  })

  React.useEffect(() => {
    if (UFE.FLAGS.liveEnabled && project.is_online && !project.finished && project.orders_count > 1) {
      poller.start()
      return () => poller.stop()
    }
  }, [])

  React.useEffect(() => {
    if (previousPercent < 100) {
      if (stats.percent >= 100) {
        renderConfetti()
      }
      setPreviousPercent(stats.percent)
    }
  }, [stats.percent])

  // We display DateInfo for:
  //- rewards projects
  //- non finished donation projects with date_end set in the future (we have to hide the component if date is passed but campaign is still ongoing)
  //- finished donation projects
  //- non finished donation projects with nb_days set
  const displayDateInfo =
    !models.project.isDonationBased(project) ||
    (project.date_end && new Date(project.date_end) >= new Date()) ||
    project.finished ||
    (!project.date_end && project.nb_days)

  return (
    <>
      <Progress
        amountRaised={stats.amountRaised}
        committed={stats.committed}
        goal={stats.goal}
        percent={stats.percent}
      />

      {!models.project.isFinished(project) && (
        <StretchGoal project={project} lang={userConfig.lang} committed={stats.committed} />
      )}

      <Line>
        {stats.ordersCount ? (
          <Orders ordersCount={stats.ordersCount} />
        ) : (
          <Supporters supportersCount={stats.supportersCount} />
        )}
        {displayDateInfo && <DateInfo />}
      </Line>
    </>
  )
}

const Line = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-direction: row;
    justify-content: space-between;
  }
`
